import { getXpsConfig } from "@amzn/aws-tc-experience-bridge-xps-registry";
import { ExperienceProviderConfig, ExperienceProvidersProvider, StaticPages } from "@amzn/aws-tc-xb-xps";
import { Environment, EnvironmentContext, Locale, useLogger } from "@amzn/xb-hooks";
import { ReactNode } from "react";
import { useInitShortbread, usePageLevelTelemetry } from "./hooks";
import { ConfigContext, XBProviderConfig } from "./hooks/useConfig";
import type { AppComponents } from "@amzn/xb-hooks";
import {
  ApolloClientProvider,
  AuthProvider,
  CloudscapeLocalizationProvider,
  DEFAULT_USE_PERSISTENT_TELEMETRY_HOOK,
  EvaluateFeatureHookProvider,
  LocaleProvider,
  LocalizationHookProvider,
  LocalizationOverride,
  LocalizationOverrideProvider,
  PersistentTelemetryDataHook,
  PersistentTelemetryDataProvider,
  ReactRouterProvider,
  AppComponentsProvider,
  RouterConfiguration,
  RoutingOverrideProvider,
  UseCookieHookProvider,
  UseLoggerHookProvider,
  UseShortbreadUiHookProvider,
  UseStaticPageHookProvider,
  UseStickyNotificationsHookProvider,
} from "./providers";
import { Tier0LiveContextProvider } from "./providers/Tier0LiveContextProvider";

/**
 * Store Initializers need access to hooks and should also
 * be called prior to "Routes".
 *
 * We pull this into it's own component so that we have access to all of the hooks
 * we provide in these functions. (If we called them prior to the render of ExperienceBridgeProvider
 * the providers won't be in the react Tree).
 */
const InitializerHooks = () => {
  const { debug } = useLogger();
  debug("Running Initializer Hooks");
  useInitShortbread();
  usePageLevelTelemetry();
  return null;
};

/**
 * ExperienceBridgeProvider is the root component that when wrapping an application enables the use of xb-hooks and Live Components.
 *
 * @param props.children - ReactNode that holds the application biz logic (classic react children)
 * @param props.config - Configuration object for XB that contains things like supported locales
 * @param props.environment - Environment enum (local, beta, gamma, prod)
 * @param props.localXpsConfig - Array of ExperienceProviderConfig objects
 * @param props.xpsOverrides - Doesn't seem to be used REMOVE_ME
 * @param props.localizationConfig - LocalizationOverride object
 * @param props.routerConfig - RouterConfiguration that allows the XB Application to use their own router
 * @param props.usePersistentTelemetryData - React hooks that initializes the telemetry data that should be emitted with all telemetry call
 * @param props.apolloClientLegacy - Temporary to allow Gandalf's host app to use undesirable behavior it was relying on.
 */
export const ExperienceBridgeProvider = ({
  children,
  config = {},
  environment = "local",
  localXpsConfig = [],
  locale,
  localizationConfig,
  routerConfig,
  usePersistentTelemetryData = DEFAULT_USE_PERSISTENT_TELEMETRY_HOOK,
  staticPages,
  appComponents,
  apolloClientLegacy,
}: {
  children: ReactNode;
  locale?: Locale;
  config?: XBProviderConfig;
  environment?: Environment;
  localXpsConfig?: ExperienceProviderConfig[];
  xpsOverrides?: ExperienceProviderConfig[];
  localizationConfig?: LocalizationOverride;
  routerConfig?: RouterConfiguration;
  usePersistentTelemetryData?: PersistentTelemetryDataHook;
  staticPages?: Partial<StaticPages>;
  appComponents?: AppComponents;
  apolloClientLegacy?: boolean;
}) => {
  const xpsConfig = getXpsConfig(environment, localXpsConfig);
  return (
    <EnvironmentContext.Provider value={{ environment }}>
      <ExperienceProvidersProvider experienceProviders={xpsConfig}>
        <AppComponentsProvider components={appComponents}>
          <UseLoggerHookProvider>
            <ConfigContext.Provider value={{ ...config }}>
              {/* Default react router provider (before react router overrides are established) */}
              <ReactRouterProvider>
                <EvaluateFeatureHookProvider>
                  <AuthProvider>
                    <UseShortbreadUiHookProvider>
                      <UseCookieHookProvider>
                        <LocaleProvider localeOverride={locale}>
                          <ApolloClientProvider apolloClientLegacy={apolloClientLegacy}>
                            <LocalizationHookProvider>
                              <LocalizationOverrideProvider override={localizationConfig}>
                                <RoutingOverrideProvider override={routerConfig}>
                                  <Tier0LiveContextProvider>
                                    <UseStickyNotificationsHookProvider>
                                      <CloudscapeLocalizationProvider>
                                        <UseStaticPageHookProvider pages={staticPages}>
                                          <PersistentTelemetryDataProvider {...{ usePersistentTelemetryData }}>
                                            <InitializerHooks />
                                            <span data-testid={"inner-xb-provider"}>{children}</span>
                                          </PersistentTelemetryDataProvider>
                                        </UseStaticPageHookProvider>
                                      </CloudscapeLocalizationProvider>
                                    </UseStickyNotificationsHookProvider>
                                  </Tier0LiveContextProvider>
                                </RoutingOverrideProvider>
                              </LocalizationOverrideProvider>
                            </LocalizationHookProvider>
                          </ApolloClientProvider>
                        </LocaleProvider>
                      </UseCookieHookProvider>
                    </UseShortbreadUiHookProvider>
                  </AuthProvider>
                </EvaluateFeatureHookProvider>
              </ReactRouterProvider>
            </ConfigContext.Provider>
          </UseLoggerHookProvider>
        </AppComponentsProvider>
      </ExperienceProvidersProvider>
    </EnvironmentContext.Provider>
  );
};
